import React, { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Form,
  Button,
  Alert,
  Spinner,
} from 'react-bootstrap';
import { FaSearch, FaCog } from 'react-icons/fa';
import { GlobalSearchSettingsApi } from '../../../api/GlobalSearchApi';
import GlobalSearchFieldsModal from './GlobalSearchFieldsModal';

/** Resolve field API names to user-facing labels from object field metadata. */
function formatFieldLabels(fieldNames, fields) {
  const byName = new Map(
    (fields || []).map((f) => [
      String(f.name || '').toLowerCase(),
      f.label || f.name,
    ])
  );
  const names = Array.isArray(fieldNames) ? fieldNames : [];
  if (names.length === 0) return '—';
  return names
    .map((name) => byName.get(String(name).toLowerCase()) || name)
    .join(', ');
}

const GlobalSearchSettings = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState('');
  const [fieldObject, setFieldObject] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const api = new GlobalSearchSettingsApi();
      const response = await api.fetchSettings();
      if (response?.data?.success) {
        setRows(response.data.data || []);
      } else {
        setError(response?.data?.message || 'Failed to load settings');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveObject = async (objectName, payload) => {
    setSaving(true);
    setError('');
    try {
      const api = new GlobalSearchSettingsApi();
      const response = await api.saveObject(objectName, payload);
      if (response?.data?.success) {
        setSuccess('Saved');
        setTimeout(() => setSuccess(''), 2500);
        await load();
        return true;
      }
      setError(response?.data?.message || 'Failed to save');
      return false;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const visible = rows.filter((r) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (
      String(r.object_label || '').toLowerCase().includes(q) ||
      String(r.object_name || '').toLowerCase().includes(q)
    );
  });

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">
                <FaSearch className="me-2" />
                Global Search Settings
              </h4>
              <Form.Control
                type="search"
                placeholder="Filter objects…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                style={{ maxWidth: 260 }}
              />
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}
              <p className="text-muted">
                Only included objects appear in header search and Cmd/Ctrl+K.
                Configure which fields are matched and which columns appear on
                the results table.
              </p>
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Include</th>
                      <th>Object</th>
                      <th>Order</th>
                      <th>Search fields</th>
                      <th>Display columns</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted">
                          No objects found.
                        </td>
                      </tr>
                    ) : (
                      visible.map((row) => (
                        <tr key={row.object_name}>
                          <td>
                            <Form.Check
                              type="switch"
                              checked={row.allow_global_search === true}
                              disabled={saving}
                              onChange={(e) =>
                                saveObject(row.object_name, {
                                  allow_global_search: e.target.checked,
                                  global_search_fields: row.global_search_fields,
                                  global_search_display_fields:
                                    row.global_search_display_fields,
                                  global_search_order: row.global_search_order,
                                })
                              }
                              aria-label={`Include ${row.object_label} in global search`}
                            />
                          </td>
                          <td>
                            <strong>{row.object_label}</strong>
                            <div className="text-muted small">{row.object_name}</div>
                          </td>
                          <td style={{ width: 90 }}>
                            <Form.Control
                              type="number"
                              size="sm"
                              value={row.global_search_order}
                              disabled={saving}
                              onBlur={(e) => {
                                const next = parseInt(e.target.value, 10) || 100;
                                if (next === row.global_search_order) return;
                                saveObject(row.object_name, {
                                  allow_global_search: row.allow_global_search,
                                  global_search_fields: row.global_search_fields,
                                  global_search_display_fields:
                                    row.global_search_display_fields,
                                  global_search_order: next,
                                });
                              }}
                              onChange={(e) => {
                                const next = parseInt(e.target.value, 10) || 0;
                                setRows((prev) =>
                                  prev.map((r) =>
                                    r.object_name === row.object_name
                                      ? { ...r, global_search_order: next }
                                      : r
                                  )
                                );
                              }}
                            />
                          </td>
                          <td className="small">
                            {formatFieldLabels(row.global_search_fields, row.fields)}
                          </td>
                          <td className="small">
                            {formatFieldLabels(
                              row.global_search_display_fields,
                              row.fields
                            )}
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => setFieldObject(row)}
                            >
                              <FaCog className="me-1" />
                              Fields
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <GlobalSearchFieldsModal
        show={Boolean(fieldObject)}
        objectConfig={fieldObject}
        saving={saving}
        onClose={() => setFieldObject(null)}
        onSave={async (payload) => {
          const ok = await saveObject(fieldObject.object_name, {
            allow_global_search: fieldObject.allow_global_search,
            global_search_order: fieldObject.global_search_order,
            ...payload,
          });
          if (ok) setFieldObject(null);
        }}
      />
    </Container>
  );
};

export default GlobalSearchSettings;
